import type { MouseEvent } from 'react';
import type { CoverageViewModel } from '@/api/types/CoverageViewModel';
import type { PolicySummaryViewModel } from 'src/api/types/PolicySummaryViewModel';

import { useParams, useNavigate } from 'react-router';
import { useMemo, useState, useCallback } from 'react';

import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Stack,
  Checkbox,
  Typography,
  IconButton,
  CardContent,
  FormControlLabel,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { formatDate } from 'src/sections/employer-data/shared/utils/policyUtils';
import { ScrollableBox } from 'src/sections/employer-data/shared/components/scrollable-box';
import { PolicyStatusChips } from 'src/sections/employer-data/shared/components/status-chips';
import { MovePolicyModal } from 'src/sections/employer-data/policy/components/move-policy-modal';

interface PolicyListProps {
  coverageSummary: CoverageViewModel;
}

export default function PolicyList({ coverageSummary }: PolicyListProps) {
  const { comboId, coverageId } = useParams<{ comboId: string; coverageId: string }>();
  const navigate = useNavigate();

  const [selectedPolicyIds, setSelectedPolicyIds] = useState<Set<number>>(new Set());
  const [movePolicyIds, setMovePolicyIds] = useState<number[]>([]);
  const [movePoliciesModalOpen, setMovePoliciesModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPolicyId, setMenuPolicyId] = useState<number | null>(null);

  const togglePolicySelection = useCallback((policyId: number) => {
    setSelectedPolicyIds((prev) => {
      const next = new Set(prev);
      if (next.has(policyId)) {
        next.delete(policyId);
      } else {
        next.add(policyId);
      }
      return next;
    });
  }, []);

  const handleMovePoliciesModalClose = useCallback(() => {
    setMovePoliciesModalOpen(false);
    setMovePolicyIds([]);
    setSelectedPolicyIds(new Set());
  }, []);

  const handlePolicyMenuOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>, policyId?: number | null) => {
      event.preventDefault();
      event.stopPropagation();

      if (policyId == null) {
        return;
      }

      setMenuAnchorEl(event.currentTarget);
      setMenuPolicyId(policyId);
    },
    []
  );

  const handlePolicyMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setMenuPolicyId(null);
  }, []);

  const handleSinglePolicyMoveOpen = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (menuPolicyId == null) {
        return;
      }

      setMovePolicyIds([menuPolicyId]);
      setMovePoliciesModalOpen(true);
      handlePolicyMenuClose();
    },
    [handlePolicyMenuClose, menuPolicyId]
  );

  const handleBulkMoveOpen = useCallback(() => {
    setMovePolicyIds(Array.from(selectedPolicyIds));
    setMovePoliciesModalOpen(true);
  }, [selectedPolicyIds]);

  const getPolicyCardTitle = (policy: PolicySummaryViewModel) => {
    const policyNumber = policy?.policyNumber ?? '';
    const employerName = policy?.primaryName?.insuredName?.trim();

    if (!employerName) {
      return policyNumber;
    }

    return `${employerName} - ${policyNumber}`;
  };

  const { allPolicies, totalCount } = useMemo(() => {
    const nonCompliantPolicies = coverageSummary?.nonCompliantPolicies ?? [];
    const compliantPolicies = coverageSummary?.compliantPolicies ?? [];
    const total = nonCompliantPolicies.length + compliantPolicies.length;

    return {
      allPolicies: {
        nonCompliant: nonCompliantPolicies,
        compliant: compliantPolicies,
      },
      totalCount: total,
    };
  }, [coverageSummary?.nonCompliantPolicies, coverageSummary?.compliantPolicies]);

  const renderPolicy = (policy: PolicySummaryViewModel, index: number) => (
    <Box
      key={policy.policyId ?? `policy-${index}`}
      component={RouterLink as React.ElementType}
      to={`/dashboard/employer-data/combo/${comboId}/coverage/${policy?.coverageId}/policy/${policy?.policyId}`}
      sx={{
        p: 1,
        paddingTop: 3,
        paddingBottom: 3,
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'shared.cardOutlined',
        position: 'relative',
        textDecoration: 'none',
        display: 'block',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <IconButton
        sx={{ position: 'absolute', top: 8, right: 8 }}
        aria-label="policy actions"
        data-testid={`policy-menu-button-${index}`}
        onClick={(e) => handlePolicyMenuOpen(e, policy.policyId)}
      >
        <MoreVertIcon />
      </IconButton>
      <Box sx={{ display: 'flex', alignItems: 'center' }} data-testid="policy-item">
        <FormControlLabel
          control={
            <Checkbox
              sx={{ ml: 1.5, mr: 1.5 }}
              data-testid="policy-checkbox"
              disabled={policy.policyId == null}
              checked={policy.policyId != null && selectedPolicyIds.has(policy.policyId)}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={() => {
                if (policy.policyId != null) {
                  togglePolicySelection(policy.policyId);
                }
              }}
            />
          }
          label={
            <Typography
              sx={{
                typography: 'subtitle1',
                color: 'text.primary',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
              data-testid={`policy-number-${index}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (policy.policyId != null && policy.coverageId != null) {
                  navigate(`/dashboard/employer-data/combo/${comboId}/coverage/${policy.coverageId}/policy/${policy.policyId}`);
                }
              }}
            >
              {getPolicyCardTitle(policy)}
            </Typography>
          }
          sx={{ mr: 1 }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
        <PolicyStatusChips
          isActive={policy?.isActive}
          isCancelled={policy?.isCancelled}
          isNonCompliant={policy?.isNonCompliant}
        />
      </Box>

      <Grid container spacing={2} columns={4} sx={{ ml: 6 }}>
        <Grid size={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 0.2 }}>
            <Typography
              component="span"
              sx={{ typography: 'body2', color: 'text.secondary' }}
              data-testid={`policy-effective-date-label-${index}`}
            >
              Effective date
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              sx={{ typography: 'subtitle2', color: 'text.primary' }}
              data-testid={`policy-effective-date-value-${index}`}
            >
              {formatDate(policy?.policyEffectiveDate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 0.2 }}>
            <Typography
              component="span"
              sx={{ typography: 'body2', color: 'text.secondary' }}
              data-testid={`policy-market-label-${index}`}
            >
              Market
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              sx={{ typography: 'subtitle2', color: 'text.primary' }}
              data-testid={`policy-market-value-${index}`}
            >
              {policy?.market ?? ''}
            </Typography>
          </Box>
        </Grid>
        <Grid size={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 0.2 }}>
            <Typography
              component="span"
              sx={{ typography: 'body2', color: 'text.secondary' }}
              data-testid={`policy-expiration-date-label-${index}`}
            >
              Expiration date
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              sx={{ typography: 'subtitle2', color: 'text.primary' }}
              data-testid={`policy-expiration-date-value-${index}`}
            >
              {formatDate(policy?.policyExpirationDate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 0.2 }}>
            <Typography
              component="span"
              sx={{ typography: 'body2', color: 'text.secondary' }}
              data-testid={`policy-usr-received-label-${index}`}
            >
              USR received
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              sx={{ typography: 'subtitle2', color: 'text.primary' }}
              data-testid={`policy-usr-received-value-${index}`}
            >
              {(policy?.usrReceived ?? false) ? 'Yes' : 'No'}
            </Typography>
          </Box>
        </Grid>
        <Grid size={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 0.2 }}>
            <Typography
              component="span"
              sx={{ typography: 'body2', color: 'text.secondary' }}
              data-testid={`policy-carrier-name-label-${index}`}
            >
              Carrier name
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              sx={{ typography: 'subtitle2', color: 'text.primary' }}
              data-testid={`policy-carrier-name-value-${index}`}
            >
              {policy?.carrierName ?? ''}
            </Typography>
          </Box>
        </Grid>
        <Grid size={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 0.2 }}>
            <Typography
              component="span"
              sx={{ typography: 'body2', color: 'text.secondary' }}
              data-testid={`policy-type-label-${index}`}
            >
              Policy type
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              component="span"
              sx={{ typography: 'subtitle2', color: 'text.primary' }}
              data-testid={`policy-type-value-${index}`}
            >
              {policy?.policyTypeEnumDescription ?? ''}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <CardContent sx={{ flexGrow: 1, pr: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ color: 'text.primary' }} data-testid="policies-header">
          Policies ({totalCount})
        </Typography>
        {selectedPolicyIds.size >= 2 && (
          <Button
            variant="contained"
            size="small"
            data-testid="move-policies-button"
            onClick={handleBulkMoveOpen}
            sx={{ bgcolor: 'grey.800', color: 'common.white', '&:hover': { bgcolor: 'grey.700' } }}
          >
            Move policies
          </Button>
        )}
      </Box>

      <ScrollableBox maxHeight="73rem" padding={2.25}>
        <Box>
          {/* Non-compliant policies section */}
          {allPolicies.nonCompliant.length > 0 && (
            <>
              <Typography component="span" sx={{ typography: 'subtitle2' }}>
                Noncompliant policies
              </Typography>
              <Stack spacing={2} sx={{ mb: 2, mt: 2 }}>
                {allPolicies.nonCompliant.map((policy, index) => renderPolicy(policy, index))}
              </Stack>
            </>
          )}

          {/* Compliant policies section */}
          {allPolicies.compliant.length > 0 && (
            <Box sx={{ mt: allPolicies.nonCompliant.length > 0 ? 3 : 0, mb: 2 }}>
              <Typography component="span" sx={{ typography: 'subtitle2' }}>
                Compliant policies
              </Typography>
              <Stack spacing={2} sx={{ mb: 2, mt: 2 }}>
                {allPolicies.compliant.map((policy, index) =>
                  renderPolicy(policy, allPolicies.nonCompliant.length + index)
                )}
              </Stack>
            </Box>
          )}
        </Box>
      </ScrollableBox>

      <MovePolicyModal
        open={movePoliciesModalOpen}
        onClose={handleMovePoliciesModalClose}
        comboId={Number(comboId)}
        coverageId={Number(coverageId)}
        policyIds={movePolicyIds}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => handlePolicyMenuClose()}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <MenuItem data-testid="policy-menu-move-policy" onClick={handleSinglePolicyMoveOpen}>
          Move policy
        </MenuItem>
      </Menu>
    </CardContent>
  );
}
